import React, { useState, useEffect } from "react";
import Store from "../../state/store";
import { inject, observer } from "mobx-react";
import { RouteComponentProps } from "react-router";
import {
  FinalQuizContainer,
  FinalQuizResultsContainer,
  ButtonFinalQuizResults,
  FinalQuizAnswersCountContainer,
  ResultCorrectionItemContainer,
  ResultCorrectionContainer,
  ResultTitle,
  SectionDescription,
  QuizComponentContainer,
} from "./FinalQuiz.style";
import { theme } from "../../Theme";
import QuizComponent, { AnswerResult, isLastQuestion, isQuestionValid } from "@upandgo/react-quiz";
import Actions from "../../state/actions";
import { ResultCorrectionItem, Certificate } from "../../components";
import { SectionFinalQuiz } from "../../types";

interface Props extends RouteComponentProps {
  store?: Store;
  mutator?: Actions;
}

const CertificateButtonAndComponent = (props: { finalQuiz: SectionFinalQuiz; actions?: Actions; store: Store }) => {
  const [isDisplayingCertificate, setDisplayingCertificate] = useState(false);

  const onSubmitIdentity = async (identity: { firstName: string; lastName: string }) => {
    if (props.actions && props.finalQuiz.shouldSendDataTo) {
      try {
        await props.actions.sendSCORMStatsToServer({
          url: props.finalQuiz.shouldSendDataTo,
          identity: identity,
          scormState: {
            completionStatus: props.store.completionStatus,
          },
        });
      } catch (e) {
        alert(e);
      }
    }
  };

  const onClickEditCertificate = () => {
    setDisplayingCertificate(true);
  };

  if (isDisplayingCertificate)
    return (
      <div style={{ marginLeft: "1em", marginRight: "1em" }}>
        <Certificate
          {...props.finalQuiz.certificate!}
          password={props.finalQuiz.certificate?.password}
          onSubmitIdentity={onSubmitIdentity}
        />
      </div>
    );
  else
    return <ButtonFinalQuizResults onClick={onClickEditCertificate}>{"Éditer mon certificat"}</ButtonFinalQuizResults>;
};

const FinalQuiz: React.FC<Props> = (props) => {
  const { completionStatus, finalQuizStartingIndex, finalQuiz } = props.store!;
  const [step, setStep] = useState(0);

  const fetchData = async () => {
    await props.mutator?.fetchCompletionStatus();
    await props.mutator?.fetchSavedFinalQuiz();
  };
  useEffect(() => {
    void fetchData();
  }, []);
  useEffect(() => {
    if (completionStatus === "completed") setStep(1);
  }, [completionStatus]);

  const onAnswerQuestion = async ({ idQuestion, idAnswers }: AnswerResult) => {
    await props.mutator?.selectFinalQuizAnswer(idQuestion, idAnswers);
    if (isLastQuestion(finalQuiz.quiz, idQuestion)) {
      setStep(1);
    }
  };

  const onClickRetry = async () => {
    await props.mutator?.resetFinalQuizAnswers();
    setStep(0);
  };

  const onClickSeeDetails = () => {
    setStep(2);
  };

  const countValidAnswers = () => {
    return finalQuiz.quiz.questionList.reduce((acc, question) => acc + (isQuestionValid(question) ? 1 : 0), 0);
  };

  return (
    <FinalQuizContainer className={"final-quiz-main-container"}>
      {finalQuiz.description && <SectionDescription>{finalQuiz.description}</SectionDescription>}

      {step === 0 && finalQuizStartingIndex !== undefined && (
        <QuizComponentContainer>
          <QuizComponent
            theme={{
              primaryColor: theme.color.primary,
            }}
            questionAggregate={finalQuiz.quiz}
            onAnswerQuestion={onAnswerQuestion}
            startingIndex={finalQuizStartingIndex}
          />
        </QuizComponentContainer>
      )}
      {step === 1 && (
        <FinalQuizResultsContainer>
          <FinalQuizAnswersCountContainer>{`${
            countValidAnswers() >= finalQuiz.threshold
              ? "Bravo, vous avez avez validé le quiz final.\n"
              : "Dommage, vous n'avez pas validé le quiz final.\n"
          }Vous avez ${countValidAnswers()} bonnes réponses sur les ${
            finalQuiz.quiz.questionList.length
          } questions.`}</FinalQuizAnswersCountContainer>

          <ButtonFinalQuizResults className={"final-quiz-result-button"} onClick={onClickRetry}>
            {"Améliorer mon score"}
          </ButtonFinalQuizResults>
          <ButtonFinalQuizResults className={"final-quiz-result-button"} onClick={onClickSeeDetails}>
            {"Voir le détails des corrections"}
          </ButtonFinalQuizResults>
          {finalQuiz.certificate && countValidAnswers() >= finalQuiz.threshold && (
            <CertificateButtonAndComponent finalQuiz={finalQuiz} actions={props.mutator} store={props.store!} />
          )}
        </FinalQuizResultsContainer>
      )}
      {step === 2 && (
        <ResultCorrectionContainer>
          <ResultTitle>{"Détails des corrections"}</ResultTitle>
          <ButtonFinalQuizResults className={"final-quiz-result-button"} onClick={onClickRetry}>
            {"Améliorer mon score"}
          </ButtonFinalQuizResults>
          <ResultCorrectionItemContainer>
            {finalQuiz.quiz.questionList.map((question, index) => {
              return (
                <ResultCorrectionItem
                  key={question.id}
                  index={index}
                  question={question.question.length > 0 ? question.question : question.context?.title || ""}
                  userAnswers={question.answerList.filter((answer) => answer.selected).map((answer) => answer.text)}
                  validAnswers={question.answerList.filter((answer) => answer.valid).map((answer) => answer.text)}
                  isValid={isQuestionValid(question)}
                />
              );
            })}
          </ResultCorrectionItemContainer>
        </ResultCorrectionContainer>
      )}
    </FinalQuizContainer>
  );
};

export default inject("store", "mutator")(observer(FinalQuiz));
